/**
 * MIME types that `canvas.toBlob()` can reliably encode. Notably excludes
 * `image/gif`, which `canvas.toBlob()` does not support.
 */
export const SUPPORTED_CROP_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Fallback output type used whenever the source type is unsupported. */
export const DEFAULT_CROP_TYPE = 'image/jpeg';

/**
 * Normalises an input MIME type to one that `canvas.toBlob()` supports.
 * Any unsupported type (e.g. `image/gif`, empty string) falls back to
 * {@link DEFAULT_CROP_TYPE}.
 */
export const resolveCropMimeType = (fileType: string): string =>
  (SUPPORTED_CROP_TYPES as readonly string[]).includes(fileType) ? fileType : DEFAULT_CROP_TYPE;

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Largest rect of `aspectRatio` (width / height) centered in a
 * `width`×`height` image, trimming the sides of a wider image or the
 * top and bottom of a taller one.
 */
export const centerCropRect = (width: number, height: number, aspectRatio: number): CropRect => {
  if (width / height > aspectRatio) {
    const cropWidth = Math.round(height * aspectRatio);
    return { x: Math.round((width - cropWidth) / 2), y: 0, width: cropWidth, height };
  }
  const cropHeight = Math.round(width / aspectRatio);
  return { x: 0, y: Math.round((height - cropHeight) / 2), width, height: cropHeight };
};

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * Wraps cropped bytes in a `File` named after the source photo. The extension
 * follows the blob's type, so a GIF re-encoded as JPEG uploads as `.jpg`.
 */
export const toCroppedFile = (blob: Blob, sourceName: string): File => {
  const extension = EXTENSION_BY_TYPE[blob.type] ?? 'jpg';
  const dot = sourceName.lastIndexOf('.');
  const baseName = dot > 0 ? sourceName.slice(0, dot) : sourceName;
  const currentExtension = dot > 0 ? sourceName.slice(dot + 1).toLowerCase() : '';
  const keepName = currentExtension === extension || (extension === 'jpg' && currentExtension === 'jpeg');
  return new File([blob], keepName ? sourceName : `${baseName}.${extension}`, { type: blob.type });
};

/** Photos within 1% of the target ratio are left as uploaded (no re-encode). */
const ASPECT_RATIO_TOLERANCE = 0.01;

/**
 * Center-crops an image file to `aspectRatio`. Returns the file untouched when
 * it already has that shape, or when the browser can't decode it.
 */
export const centerCropImageFile = async (file: File, aspectRatio: number): Promise<File> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    const { width, height } = bitmap;
    if (Math.abs(width / height - aspectRatio) / aspectRatio < ASPECT_RATIO_TOLERANCE) return file;

    const rect = centerCropRect(width, height, aspectRatio);
    const canvas = document.createElement('canvas');
    canvas.width = rect.width;
    canvas.height = rect.height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, rect.x, rect.y, rect.width, rect.height, 0, 0, rect.width, rect.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, resolveCropMimeType(file.type))
    );
    // Release the canvas's pixel buffer now rather than whenever it's collected.
    canvas.width = 0;
    canvas.height = 0;
    return blob ? toCroppedFile(blob, file.name) : file;
  } finally {
    bitmap.close();
  }
};
